import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import {
  getConsentPreferences,
  storeConsentPreferences,
  type ConsentPreferences,
} from '@/utils/cookie-consent';

interface PreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

export const PreferencesModal = ({
  isOpen,
  onClose,
  onSave,
}: PreferencesModalProps) => {
  const { t } = useTranslation('cookie-consent');
  const modalRef = useRef<HTMLDivElement>(null);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    timestamp: 0,
    version: '1.0',
  });

  // Load existing preferences on mount
  useEffect(() => {
    if (isOpen) {
      const current = getConsentPreferences();
      setPreferences(current);
    }
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      firstElement?.focus();

      return () => {
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isOpen]);

  const handleSave = () => {
    storeConsentPreferences(preferences);
    onSave();
    onClose();
  };

  const handleToggle = (category: 'analytics' | 'marketing') => {
    setPreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-preferences-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className="relative bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-base-100 border-b border-base-300 px-6 py-4 flex items-center justify-between">
          <h2
            id="cookie-preferences-title"
            className="text-xl font-semibold text-base-content"
          >
            {t('customize')}
          </h2>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-circle"
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Essential Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="essential-checkbox" className="flex items-center gap-3 flex-1">
                <input
                  id="essential-checkbox"
                  type="checkbox"
                  checked={preferences.essential}
                  disabled
                  className="checkbox checkbox-primary"
                  aria-describedby="essential-description"
                />
                <div className="flex-1">
                  <div className="font-medium text-base-content">
                    {t('essentialLabel')}
                  </div>
                  <div
                    id="essential-description"
                    className="text-sm text-base-content/70"
                  >
                    {t('essentialDescription')}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="analytics-checkbox" className="flex items-center gap-3 flex-1">
                <input
                  id="analytics-checkbox"
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => handleToggle('analytics')}
                  className="checkbox checkbox-primary"
                  aria-describedby="analytics-description"
                />
                <div className="flex-1">
                  <div className="font-medium text-base-content">
                    {t('analyticsLabel')}
                  </div>
                  <div
                    id="analytics-description"
                    className="text-sm text-base-content/70"
                  >
                    {t('analyticsDescription')}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label htmlFor="marketing-checkbox" className="flex items-center gap-3 flex-1">
                <input
                  id="marketing-checkbox"
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() => handleToggle('marketing')}
                  className="checkbox checkbox-primary"
                  aria-describedby="marketing-description"
                />
                <div className="flex-1">
                  <div className="font-medium text-base-content">
                    {t('marketingLabel')}
                  </div>
                  <div
                    id="marketing-description"
                    className="text-sm text-base-content/70"
                  >
                    {t('marketingDescription')}
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-base-100 border-t border-base-300 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn btn-ghost"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};
